import React from 'react';
import { getEquipmentIconKey, EquipmentIconKey } from './equipmentIconMapping';

interface IconProps {
  size: number;
  className?: string;
}

interface EquipmentIconProps {
  code?: string;
  description?: string;
  size?: number;
  className?: string;
}

// ── Shared SVG wrapper ──
const S = ({ size, className, children }: { size: number; className?: string; children: React.ReactNode }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round"
    className={className} style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle' }}
  >{children}</svg>
);

// ═══════════════════════════════════════════════════════════════
// Custom SVG icons — simple outline silhouettes using currentColor
// ═══════════════════════════════════════════════════════════════

const ExcavatorIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Tracks */}
    <path d="M2 20h12a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2H2" />
    <line x1="2" y1="16" x2="2" y2="20" />
    {/* Cab */}
    <rect x="3" y="12" width="5" height="4" rx="0.5" />
    {/* Boom + stick + bucket */}
    <polyline points="8,12 15,5 20,13 17,15" />
  </S>
);

const DozerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Tracks */}
    <path d="M4 20h14a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2z" />
    {/* Cab */}
    <rect x="10" y="11" width="7" height="5" rx="0.5" />
    {/* Blade */}
    <path d="M3 16V10l2-2" />
  </S>
);

const LoaderIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Body */}
    <rect x="8" y="11" width="10" height="6" rx="1" />
    {/* Wheels */}
    <circle cx="10" cy="19" r="2" />
    <circle cx="16" cy="19" r="2" />
    {/* Bucket arm */}
    <polyline points="8,12 4,8 2,8 2,12 4,12" />
  </S>
);

const ForkliftIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Body */}
    <rect x="6" y="10" width="10" height="8" rx="1" />
    {/* Wheels */}
    <circle cx="8" cy="20" r="2" />
    <circle cx="14" cy="20" r="2" />
    {/* Mast */}
    <line x1="4" y1="18" x2="4" y2="4" />
    {/* Forks */}
    <path d="M4 16H2M4 13H2" />
    {/* Overhead guard */}
    <line x1="6" y1="8" x2="16" y2="8" />
  </S>
);

const SkidSteerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Wheels */}
    <circle cx="14" cy="18" r="2.5" />
    <circle cx="20" cy="18" r="2.5" />
    {/* Cab body – trapezoid leaning back */}
    <path d="M12 17V10l4-3h6v10" />
    {/* Window lines */}
    <line x1="14" y1="8" x2="14" y2="12" />
    <line x1="14" y1="8" x2="18" y2="8" />
    {/* Lift arm from cab top forward to bucket */}
    <line x1="13" y1="9" x2="4" y2="12" />
    {/* Bucket – scoop shape angling down */}
    <path d="M4 12L1 21h7L6 14z" />
    {/* Bucket teeth */}
    <line x1="2" y1="20" x2="3" y2="18" />
    <line x1="4" y1="20" x2="5" y2="18" />
    <line x1="6" y1="20" x2="7" y2="18" />
  </S>
);

const BackhoeIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Wheels */}
    <circle cx="6" cy="19" r="2" />
    <circle cx="16" cy="19" r="3" />
    {/* Body */}
    <rect x="4" y="12" width="14" height="5" rx="1" />
    {/* Boom arm (rear) */}
    <polyline points="18,12 21,6 22,10" />
    {/* Front bucket */}
    <path d="M4 14L2 12v3" />
  </S>
);

const CompactorIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Rear body */}
    <rect x="12" y="10" width="8" height="7" rx="1" />
    {/* Rear wheel */}
    <circle cx="16" cy="19" r="2" />
    {/* Drum (front roller) */}
    <ellipse cx="6" cy="17" rx="4" ry="3" />
    {/* Arm connecting body to drum */}
    <line x1="12" y1="13" x2="8" y2="15" />
  </S>
);

const TrencherIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Body */}
    <rect x="3" y="10" width="12" height="6" rx="1" />
    {/* Tracks */}
    <rect x="2" y="16" width="14" height="4" rx="2" />
    {/* Digging chain */}
    <path d="M15 14L20 20M17 13l2 2M19 12l2 2" />
  </S>
);

const WaterTruckIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Cab */}
    <rect x="2" y="11" width="5" height="6" rx="0.5" />
    {/* Tank (cylinder) */}
    <ellipse cx="14" cy="14" rx="7" ry="4" />
    {/* Wheels */}
    <circle cx="5" cy="19" r="2" />
    <circle cx="17" cy="19" r="2" />
  </S>
);

const AirCompressorIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Body */}
    <rect x="2" y="9" width="16" height="8" rx="1" />
    {/* Tongue/hitch */}
    <line x1="18" y1="15" x2="22" y2="15" />
    {/* Wheels */}
    <circle cx="6" cy="19" r="2" />
    <circle cx="14" cy="19" r="2" />
    {/* Hose port */}
    <circle cx="5" cy="13" r="1.5" />
  </S>
);

const ManLiftIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Base */}
    <rect x="4" y="18" width="12" height="3" rx="1" />
    {/* Boom */}
    <line x1="10" y1="18" x2="16" y2="6" />
    {/* Platform/basket */}
    <rect x="14" y="3" width="6" height="4" rx="0.5" />
    {/* Wheels */}
    <circle cx="7" cy="22" r="1" />
    <circle cx="13" cy="22" r="1" />
  </S>
);

const PipelayerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Tracks */}
    <path d="M2 20h16a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2H2" />
    <line x1="2" y1="16" x2="2" y2="20" />
    {/* Body */}
    <rect x="4" y="12" width="10" height="4" rx="0.5" />
    {/* Side boom */}
    <line x1="8" y1="12" x2="20" y2="4" />
    {/* Hook */}
    <path d="M20 4v3" />
  </S>
);

const DirectionalDrillIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Body */}
    <rect x="2" y="12" width="12" height="6" rx="1" />
    {/* Tracks */}
    <rect x="1" y="18" width="14" height="3" rx="1.5" />
    {/* Drill rod (angled) */}
    <line x1="14" y1="14" x2="22" y2="8" />
    {/* Drill bit */}
    <path d="M22 8l-1 2M22 8l1 1" />
  </S>
);

const GraderIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Long body */}
    <path d="M2 14h18l2-4h-4" />
    <rect x="14" y="10" width="6" height="4" rx="0.5" />
    {/* Blade (angled under body) */}
    <line x1="7" y1="18" x2="13" y2="16" />
    {/* Wheels */}
    <circle cx="5" cy="18" r="2" />
    <circle cx="17" cy="18" r="2.5" />
  </S>
);

// ═══════════════════════════════════════════════════════════════
// Generic / Lucide-style fallback icons
// ═══════════════════════════════════════════════════════════════

const TruckIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    {/* Cab: hood, windshield, roof */}
    <path d="M2 17V13h3V10l3-3h4v6" />
    {/* Cab roof */}
    <line x1="8" y1="7" x2="12" y2="7" />
    {/* Cab back wall */}
    <line x1="12" y1="7" x2="12" y2="13" />
    {/* Bed floor + walls */}
    <path d="M12 13h10v4H2" />
    {/* Bed back wall (tailgate) */}
    <line x1="22" y1="10" x2="22" y2="17" />
    {/* Bed side rail */}
    <line x1="12" y1="10" x2="22" y2="10" />
    {/* Wheels */}
    <circle cx="6" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
  </S>
);

const TrailerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="1" y="10" width="19" height="7" rx="1" />
    <line x1="20" y1="15" x2="23" y2="15" />
    <circle cx="5" cy="19" r="2" /><circle cx="15" cy="19" r="2" />
  </S>
);

const GeneratorIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    {/* Lightning bolt */}
    <polyline points="13,9 11,12 14,12 11,16" />
    <circle cx="7" cy="20" r="1.5" /><circle cx="17" cy="20" r="1.5" />
  </S>
);

const WelderIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="3" y="8" width="14" height="10" rx="2" />
    {/* Spark/arc lines */}
    <path d="M20 10l-2 3 3 1-2 3" />
    <circle cx="7" cy="20" r="1.5" /><circle cx="13" cy="20" r="1.5" />
  </S>
);

const TractorIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="6" y="10" width="10" height="6" rx="1" />
    <circle cx="5" cy="18" r="2" />
    <circle cx="17" cy="17" r="4" />
    <line x1="6" y1="10" x2="4" y2="8" />
  </S>
);

const ContainerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="2" y="6" width="20" height="14" rx="1" />
    <line x1="8" y1="6" x2="8" y2="20" />
    <line x1="16" y1="6" x2="16" y2="20" />
    <line x1="2" y1="13" x2="22" y2="13" />
  </S>
);

const SuvIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <path d="M3 14h18l-1-4h-5l-2-3H7L5 10H4z" />
    <rect x="2" y="14" width="20" height="4" rx="1" />
    <circle cx="7" cy="19" r="2" /><circle cx="17" cy="19" r="2" />
  </S>
);

const ToolVanIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <path d="M2 8h14v10H2z" />
    <path d="M16 11h4l2 3v4h-6z" />
    <circle cx="6" cy="20" r="2" /><circle cx="18" cy="20" r="2" />
  </S>
);

const FusionMachineIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="3" y="8" width="18" height="10" rx="2" />
    {/* Two pipes joining */}
    <line x1="1" y1="13" x2="8" y2="13" />
    <line x1="16" y1="13" x2="23" y2="13" />
    <circle cx="12" cy="13" r="3" />
  </S>
);

const MowerIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="6" y="8" width="12" height="8" rx="1" />
    <line x1="12" y1="4" x2="12" y2="8" />
    <circle cx="8" cy="18" r="2" /><circle cx="16" cy="18" r="2" />
    <line x1="6" y1="16" x2="2" y2="18" />
  </S>
);

const CrawlerCarrierIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="2" y="17" width="20" height="4" rx="2" />
    <rect x="3" y="10" width="18" height="7" rx="1" />
  </S>
);

const PressureWasherIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="4" y="6" width="12" height="12" rx="2" />
    <circle cx="10" cy="20" r="2" />
    <path d="M16 10h4l2-3" />
  </S>
);

const PressurePumpIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <circle cx="10" cy="13" r="5" />
    <line x1="15" y1="13" x2="22" y2="13" />
    <line x1="22" y1="10" x2="22" y2="16" />
    <rect x="5" y="18" width="10" height="3" rx="1" />
  </S>
);

const ThreadingMachineIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="2" y="8" width="14" height="10" rx="2" />
    {/* Threaded rod */}
    <line x1="16" y1="13" x2="23" y2="13" />
    <path d="M17 11v4M19 11v4M21 11v4" />
  </S>
);

const AtvIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <rect x="5" y="10" width="14" height="6" rx="2" />
    <circle cx="6" cy="18" r="3" /><circle cx="18" cy="18" r="3" />
    <line x1="10" y1="10" x2="10" y2="7" />
    <line x1="8" y1="7" x2="12" y2="7" />
  </S>
);

const AttachmentIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <path d="M12 3v14" />
    <path d="M8 10a4 4 0 0 1 8 0" />
    <path d="M6 17h12" />
    <path d="M8 17v3M16 17v3" />
  </S>
);

const MiscIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </S>
);

const DefaultIcon = ({ size, className }: IconProps) => (
  <S size={size} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4l2 2" />
  </S>
);

// ═══════════════════════════════════════════════════════════════
// Icon lookup table
// ═══════════════════════════════════════════════════════════════

const iconMap: Record<EquipmentIconKey, (props: IconProps) => JSX.Element> = {
  excavator: ExcavatorIcon,
  dozer: DozerIcon,
  loader: LoaderIcon,
  forklift: ForkliftIcon,
  skidSteer: SkidSteerIcon,
  backhoe: BackhoeIcon,
  compactor: CompactorIcon,
  trencher: TrencherIcon,
  waterTruck: WaterTruckIcon,
  airCompressor: AirCompressorIcon,
  manLift: ManLiftIcon,
  pipelayer: PipelayerIcon,
  directionalDrill: DirectionalDrillIcon,
  grader: GraderIcon,
  truck: TruckIcon,
  trailer: TrailerIcon,
  generator: GeneratorIcon,
  welder: WelderIcon,
  tractor: TractorIcon,
  container: ContainerIcon,
  suv: SuvIcon,
  toolVan: ToolVanIcon,
  fusionMachine: FusionMachineIcon,
  mower: MowerIcon,
  crawlerCarrier: CrawlerCarrierIcon,
  pressureWasher: PressureWasherIcon,
  pressurePump: PressurePumpIcon,
  threadingMachine: ThreadingMachineIcon,
  atv: AtvIcon,
  attachment: AttachmentIcon,
  misc: MiscIcon,
  default: DefaultIcon,
};

// ═══════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════

export const EquipmentIcon: React.FC<EquipmentIconProps> = ({
  code,
  description,
  size = 16,
  className,
}) => {
  const key = getEquipmentIconKey(code, description);
  const Renderer = iconMap[key] || iconMap.default;
  return <Renderer size={size} className={className} />;
};

export default EquipmentIcon;

// ═══════════════════════════════════════════════════════════════
// Example usages:
//
// <EquipmentIcon description="Excavator - 2-6 Ton St Bkt" size={14} />
// <EquipmentIcon code="TK-WT-4000" size={16} />
// <EquipmentIcon code="FL-TH-10K" description="Forklift - Telehandler" size={18} />
// <EquipmentIcon description="Generator - 30.0 KW" />
// <EquipmentIcon description="Trailer - 55 Ton Lowboy" size={14} />
// ═══════════════════════════════════════════════════════════════
